import { routes } from '@/shared/routes';
import { SaveConfirmRule } from '@/shared/types/saveConfirm';
import { pick } from '@/shared/utils/object';

import { close } from '@/front/actions/ui';
import { execute } from '@/front/actions/utils';
import { getFormData } from '@/front/editable/state';
import { DELETE, POST } from '@/front/fetch';
import { lockSavedForm, saveForm } from '@/front/formSave';
import { confirmModal, showModal } from '@/front/modal';
import { hideOffcanvas } from '@/front/offcanvas';

export const submit = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form') as HTMLFormElement | null;

  if (form && form.dataset.submitted !== 'true') {
    form.dataset.submitted = 'true';
    form.submit();
  }
};

export const postForm = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form');

  if (form) {
    const data = Object.fromEntries(new FormData(form));

    const { res, status } = await execute(POST, form.action, data);

    delete form.dataset.dirty;

    if (res.ok) {
      lockSavedForm(form, !!status?.redirectUrl);

      if (status?.redirectUrl) {
        window.location.href = status?.redirectUrl;
      } else {
        // HACK: Assumes we're in offcanvas - might not be?
        // HACK: Assume there are grids = might not be?
        hideOffcanvas({ refreshGrids: true });
      }
    }
  }
};

const parseSaveConfirmRules = (raw: string | undefined): SaveConfirmRule[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as SaveConfirmRule | SaveConfirmRule[];
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

const matchSaveConfirmRule = (rules: SaveConfirmRule[], data: Record<string, unknown>): SaveConfirmRule | undefined =>
  rules.find((rule) => rule.when && rule.when.values.includes(data[rule.when.field] as string)) ??
  rules.find((rule) => !rule.when);

export const save = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest<HTMLFormElement>('form');

  if (form) {
    const rules = parseSaveConfirmRules(form.dataset.saveConfirm);

    if (rules.length > 0) {
      const data = await getFormData(form);
      const rule = matchSaveConfirmRule(rules, data);

      if (rule) {
        let shouldConfirm = true;

        if (rule.emptyFields || rule.requireAnyOf) {
          const missingRequired = rule.emptyFields ? rule.emptyFields.some((field) => data[field] == null) : false;
          const missingAnyOf = rule.requireAnyOf ? rule.requireAnyOf.every((field) => data[field] == null) : false;
          shouldConfirm = missingRequired || missingAnyOf;
        }

        if (shouldConfirm && !(await confirmModal(rule.message, 'Save'))) {
          return;
        }
      }
    }

    await saveForm(form);
  }
};

export const del = async () => {
  const { res, status } = await execute(DELETE, window.location.href);

  if (res.ok) {
    if (status?.redirectUrl) {
      window.location.href = status?.redirectUrl;
    } else {
      close();
    }
  }
};

export const previewTemplate = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form') as HTMLFormElement | null;

  if (form) {
    const data = pick(await getFormData(form), 'subject', 'content');

    const res = await POST(routes.emailTemplates.preview(), data);

    const { subject, content, signature } = await res.json();

    showModal({
      title: subject,
      body: content ? `${content}${signature ?? ''}` : signature,
      fullScreen: true,
    });
  }
};
